import { useState, type FormEvent } from "react";
import { submitStory } from "@/lib/storySubmissions";

type Status = "idle" | "loading" | "success" | "error";

const CATEGORIES = [
  "Farm Life",
  "Farmers",
  "Travel",
  "Food",
  "Seasonal",
  "Traditions",
  "Guides",
];

export default function ShareStoryForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const phone = String(formData.get("phone") || "").trim();
    const village = String(formData.get("village") || "").trim();
    const title = String(formData.get("title") || "").trim();
    const category = String(formData.get("category") || "").trim();
    const story = String(formData.get("story") || "").trim();

    if (!name || !email || !title || !story) {
      setStatus("error");
      setError("Please add your name, email, a title and your story before sending.");
      return;
    }

    setStatus("loading");
    setError("");

    try {
      await submitStory({ submitter_name: name, email, phone, village, title, category, story });
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setError("We couldn't send your story just now. Please try again in a moment.");
    }
  };

  const inputClass =
    "w-full bg-background-100 border border-background-300 rounded-md px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400";
  const labelClass = "block text-sm font-medium text-foreground-800 mb-2";

  if (status === "success") {
    return (
      <div className="bg-background-50 border border-background-200 rounded-lg p-8 md:p-12 text-center">
        <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className="ri-heart-3-line text-3xl"></i>
        </span>
        <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold mt-6">
          Thank you for sharing.
        </h3>
        <p className="text-foreground-600 leading-relaxed mt-3 max-w-md mx-auto">
          Your story is now with our editorial team. If it's a good fit, we'll polish it and feature
          it in the journal — we'll reach out by email before anything is published.
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-7 inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line"></i> Share another story
        </button>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-9">
      <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold">
        Send Us Your Story
      </h2>
      <p className="text-sm text-foreground-600 leading-relaxed mt-2">
        Fields marked with a red asterisk are required. Write as little or as much as you like.
      </p>

      <form id="share-story-form" onSubmit={handleSubmit} className="mt-7 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ss-name" className={labelClass}>
              Your name <span className="text-accent-600">*</span>
            </label>
            <input
              id="ss-name"
              name="name"
              type="text"
              required
              placeholder="e.g. Lakshmi Prasanna"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="ss-email" className={labelClass}>
              Email <span className="text-accent-600">*</span>
            </label>
            <input
              id="ss-email"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ss-phone" className={labelClass}>
              Phone
            </label>
            <input
              id="ss-phone"
              name="phone"
              type="tel"
              placeholder="+91 ..."
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="ss-village" className={labelClass}>
              Village / place
            </label>
            <input
              id="ss-village"
              name="village"
              type="text"
              placeholder="e.g. Amalapuram"
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="ss-title" className={labelClass}>
              Story title <span className="text-accent-600">*</span>
            </label>
            <input
              id="ss-title"
              name="title"
              type="text"
              required
              placeholder="e.g. The grandmother who plants by the moon"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="ss-category" className={labelClass}>
              Category
            </label>
            <select id="ss-category" name="category" defaultValue="Farm Life" className={`${inputClass} cursor-pointer`}>
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="ss-story" className={labelClass}>
            Your story <span className="text-accent-600">*</span>
          </label>
          <textarea
            id="ss-story"
            name="story"
            rows={7}
            maxLength={3000}
            required
            placeholder="Tell us what you saw, felt and learned…"
            className={`${inputClass} resize-none`}
          ></textarea>
          <p className="text-xs text-foreground-500 mt-1.5">Up to 3000 characters.</p>
        </div>

        <div className="form-extra-contact" aria-hidden="true">
          <label htmlFor="ss-website-alt">Leave this field empty</label>
          <input
            id="ss-website-alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>

        {status === "error" && error ? (
          <p className="flex items-start gap-2 text-sm text-accent-900 bg-accent-100 border border-accent-300 rounded-md px-4 py-3">
            <i className="ri-error-warning-line text-lg"></i>
            <span>{error}</span>
          </p>
        ) : null}

        <button
          type="submit"
          disabled={status === "loading"}
          className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-6 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i> Sending…
            </>
          ) : (
            <>
              Send my story <i className="ri-send-plane-fill"></i>
            </>
          )}
        </button>
      </form>
    </div>
  );
}